import { useCallback, useState } from 'react';
import { useAtom, useStore } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { focusAtom } from 'jotai-optics';
import { clear } from 'suspend-react';
import GroupConfigurationAPI from 'services/API/GroupConfigurationAPI';
import { useSelectedPolygonBoundsOnMap } from 'state/map';
import { useSelectedGroupQuery } from 'state/queryParams';
import { Group, CityUpdateKeys, TStartDate, EmailFrequency } from 'types/misc';
import {
  GROUP_TYPE,
  NEW_GROUP_ID,
  DEFAULT_VERTICAL_EXCLUSIONS,
  getDefaultEndDate,
} from 'utils/constants';
import { groupToGroupRaw } from 'utils/transforms';

// Atoms
const storage = createJSONStorage<Group>(() => sessionStorage);

const DEFAULT_NOTIFICATIONS = {
  enabled: true,
  timezone: '',
};

export const DEFAULT_GROUP = {
  id: '',
  name: '',
  districts: [],
  zoneExclusions: [],
  verticals: [],
  verticalsExclusions: DEFAULT_VERTICAL_EXCLUSIONS,
  companies: [],
  triggers: [],
  past_project_stage: [],
  exclude_past_project_stage: [],
  workTypes: [],
  workTypesExclusions: [],
  keywords: '',
  exclusions: '',
  status: 'active',
  type: GROUP_TYPE.PROJECT,
  cityIds: [],
  county_codes: [],
  state_code: null,
  mercator_metropolitan_ids: [],
  mercatorMetropolitanId: null,
  cost: {
    min: 0,
    max: 0,
  },
  project_cost_include_unknown: true,
  startDate: 'anytime' as TStartDate,
  endDate: getDefaultEndDate(),
  date_created_start: 'anytime' as TStartDate,
  date_created_end: getDefaultEndDate(),
  notifications: DEFAULT_NOTIFICATIONS,
  email_frequency: 'weekly' as EmailFrequency,
  realEstate: false,
  brokerData: false,
  documentUrl: false,
  project_size_min: null,
  project_size_max: null,
  project_size_include_unknown: false,
  parcel_size_min: null,
  parcel_size_max: null,
  parcel_size_include_unknown: true,
  companyKeywords: '',
  companyKeywordsExclusions: '',
  company_stakeholder_types: '',
  company_stakeholder_types_exclusions: '',
  polygon_bounds: '',
};

// Add new atom to hold current user state selection: string

export const groupAtom = atomWithStorage('dashboardGroup', DEFAULT_GROUP, storage);

groupAtom.debugLabel = 'dashboardGroup';

const userStateSelectionAtom = focusAtom(groupAtom, (optic) => optic.prop('state_code'));
userStateSelectionAtom.debugLabel = 'state_code';

const userMultiRegionSelectionAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('mercator_metropolitan_ids'),
);
userMultiRegionSelectionAtom.debugLabel = 'userMultiRegionSelection';

// Use these atoms to update the main state
const groupIdAtom = focusAtom(groupAtom, (optic) => optic.prop('id'));
groupIdAtom.debugLabel = 'dashboardGroupId';
const groupNameAtom = focusAtom(groupAtom, (optic) => optic.prop('name'));
groupNameAtom.debugLabel = 'dashboardGroupName';
const groupTypeAtom = focusAtom(groupAtom, (optic) => optic.prop('type'));
groupTypeAtom.debugLabel = 'dashboardGroupType';
const groupNotificationsAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('notifications'),
);
groupNotificationsAtom.debugLabel = 'dashboardGroupNotifications';
const groupEmailFrequencyAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('email_frequency'),
);
groupEmailFrequencyAtom.debugLabel = 'dashboardGroupEmailFrequency';
const costAtom = focusAtom(groupAtom, (optic) => optic.prop('cost'));
costAtom.debugLabel = 'dashboardGroupCost';
const costIncludeUnknownAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('project_cost_include_unknown'),
);
const groupDistrictsAtom = focusAtom(groupAtom, (optic) => optic.prop('districts'));
groupDistrictsAtom.debugLabel = 'dashboardDistricts';
const groupZoneExclusionsAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('zoneExclusions'),
);
groupZoneExclusionsAtom.debugLabel = 'dashboardZoneExclusions';
const groupVerticalsAtom = focusAtom(groupAtom, (optic) => optic.prop('verticals'));
groupVerticalsAtom.debugLabel = 'dashboardVerticals';
const groupVerticalsExclusionsAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('verticalsExclusions'),
);
groupVerticalsExclusionsAtom.debugLabel = 'dashboardVerticalsExclusions';
const groupCompaniesAtom = focusAtom(groupAtom, (optic) => optic.prop('companies'));
groupCompaniesAtom.debugLabel = 'dashboardCompanies';
const groupTriggersAtom = focusAtom(groupAtom, (optic) => optic.prop('triggers'));
groupTriggersAtom.debugLabel = 'dashboardTriggers';
const groupPastProjectStageAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('past_project_stage'),
);
groupPastProjectStageAtom.debugLabel = 'dashboardPastProjectStage';
const groupExcludePastProjectStageAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('exclude_past_project_stage'),
);
groupExcludePastProjectStageAtom.debugLabel = 'dashboardExcludePastProjectStage';
const groupKeywordsAtom = focusAtom(groupAtom, (optic) => optic.prop('keywords'));
const groupExclusionsAtom = focusAtom(groupAtom, (optic) => optic.prop('exclusions'));
const groupStatusAtom = focusAtom(groupAtom, (optic) => optic.prop('status'));
const startDateAtom = focusAtom(groupAtom, (optic) => optic.prop('startDate'));
const endDateAtom = focusAtom(groupAtom, (optic) => optic.prop('endDate'));
groupKeywordsAtom.debugLabel = 'dashboardKeywords';
const realEstateDataAtom = focusAtom(groupAtom, (optic) => optic.prop('realEstate'));
const brokerDataAtom = focusAtom(groupAtom, (optic) => optic.prop('brokerData'));
const documentUrlAtom = focusAtom(groupAtom, (optic) => optic.prop('documentUrl'));
const createdAtStart = focusAtom(groupAtom, (optic) => optic.prop('date_created_start'));
const createdAtEnd = focusAtom(groupAtom, (optic) => optic.prop('date_created_end'));
const workTypesAtom = focusAtom(groupAtom, (optic) => optic.prop('workTypes'));
const workTypesExclusionsAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('workTypesExclusions'),
);
const projectSizeMinAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('project_size_min'),
);
const projectSizeMaxAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('project_size_max'),
);
const projectSizeIncludeUnknownAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('project_size_include_unknown'),
);
const parcelSizeMinAtom = focusAtom(groupAtom, (optic) => optic.prop('parcel_size_min'));
const parcelSizeMaxAtom = focusAtom(groupAtom, (optic) => optic.prop('parcel_size_max'));
const parcelSizeIncludeUnknownAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('parcel_size_include_unknown'),
);
const groupCompanyKeywordsAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('companyKeywords'),
);
const groupCompanyKeywordsExclusionsAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('companyKeywordsExclusions'),
);
const companyStakeholderTypesAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('company_stakeholder_types'),
);
const companyStakeholderTypesExclusionsAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('company_stakeholder_types_exclusions'),
);
const mercatorMetropolitanIdAtom = focusAtom(groupAtom, (optic) =>
  optic.prop('mercatorMetropolitanId'),
);
const cityIdsAtom = focusAtom(groupAtom, (optic) => optic.prop('cityIds'));
const countyCodesAtom = focusAtom(groupAtom, (optic) => optic.prop('county_codes'));
const polygonBoundsAtom = focusAtom(groupAtom, (optic) => optic.prop('polygon_bounds'));

// hooks
export const useDashboardGroupId = () => useAtom(groupIdAtom);
export const useDashboardGroupName = () => useAtom(groupNameAtom);
export const useDashboardGroupType = () => useAtom(groupTypeAtom);
export const useDashboardGroupNotifcations = () => useAtom(groupNotificationsAtom);
export const useEmailFrequency = () => useAtom(groupEmailFrequencyAtom);
export const useDashboardCostState = () => useAtom(costAtom);
export const useDashboardCostIncludeUnknownState = () => useAtom(costIncludeUnknownAtom);
export const useDashboardDistricts = () => useAtom(groupDistrictsAtom);
export const useDashboardZoneExclusions = () => useAtom(groupZoneExclusionsAtom);
export const useDashboardVerticals = () => useAtom(groupVerticalsAtom);
export const useDashboardVerticalsExclusions = () =>
  useAtom(groupVerticalsExclusionsAtom);
export const useDashboardCompaniesState = () => useAtom(groupCompaniesAtom);
export const useDashboardTriggers = () => useAtom(groupTriggersAtom);
export const useDashboardPastProjectStage = () => useAtom(groupPastProjectStageAtom);
export const useDashboardExcludePastProjectStage = () =>
  useAtom(groupExcludePastProjectStageAtom);
export const useDashboardKeywords = () => useAtom(groupKeywordsAtom);
export const useDashboardExclusions = () => useAtom(groupExclusionsAtom);
export const useDashboardCompanyKeywords = () => useAtom(groupCompanyKeywordsAtom);
export const useDashboardCompanyKeywordsExclusions = () =>
  useAtom(groupCompanyKeywordsExclusionsAtom);
export const useDashboardCompanyStakeholderTypes = () =>
  useAtom(companyStakeholderTypesAtom);
export const useDashboardCompanyStakeholderTypesExclusions = () =>
  useAtom(companyStakeholderTypesExclusionsAtom);

export const useDashboardStatus = () => useAtom(groupStatusAtom);
export const useDashboardStartDate = () => useAtom(startDateAtom);
export const useDashboardEndDate = () => useAtom(endDateAtom);
export const useRealEstateData = () => useAtom(realEstateDataAtom);
export const useBrokerData = () => useAtom(brokerDataAtom);
export const useDocumentUrl = () => useAtom(documentUrlAtom);
export const useCreatedAtStart = () => useAtom(createdAtStart);
export const useCreatedAtEnd = () => useAtom(createdAtEnd);
export const useWorkTypes = () => useAtom(workTypesAtom);
export const useWorkTypesExclusions = () => useAtom(workTypesExclusionsAtom);
export const useMercatorMetropolitanId = () => useAtom(mercatorMetropolitanIdAtom);
export const useCityIds = () => useAtom(cityIdsAtom);
export const useUserCountySelection = () => useAtom(countyCodesAtom);
export const usePolygonBounds = () => useAtom(polygonBoundsAtom);
const useProjectSizeMin = () => useAtom(projectSizeMinAtom);
const useProjectSizeMax = () => useAtom(projectSizeMaxAtom);
const useProjectSizeIncludeUnknown = () => useAtom(projectSizeIncludeUnknownAtom);
const useParcelSizeMin = () => useAtom(parcelSizeMinAtom);
const useParcelSizeMax = () => useAtom(parcelSizeMaxAtom);
const useParcelSizeIncludeUnknown = () => useAtom(parcelSizeIncludeUnknownAtom);

export const useUserMultiRegionSelection = () => useAtom(userMultiRegionSelectionAtom);
export const useUserStateSelection = () => useAtom(userStateSelectionAtom);

export const useProjectSize = () => {
  const [projectSizeMin, setProjectSizeMin] = useProjectSizeMin();
  const [projectSizeMax, setProjectSizeMax] = useProjectSizeMax();
  const [projectSizeIncludeUnknown, setProjectSizeIncludeUnknown] =
    useProjectSizeIncludeUnknown();

  return {
    projectSizeMin,
    setProjectSizeMin,
    projectSizeMax,
    setProjectSizeMax,
    projectSizeIncludeUnknown,
    setProjectSizeIncludeUnknown,
  };
};

export const useParcelSize = () => {
  const [parcelSizeMin, setParcelSizeMin] = useParcelSizeMin();
  const [parcelSizeMax, setParcelSizeMax] = useParcelSizeMax();
  const [parcelSizeIncludeUnknown, setParcelSizeIncludeUnknown] =
    useParcelSizeIncludeUnknown();

  return {
    parcelSizeMin,
    setParcelSizeMin,
    parcelSizeMax,
    setParcelSizeMax,
    parcelSizeIncludeUnknown,
    setParcelSizeIncludeUnknown,
  };
};

export const useClearGroup = () => {
  const [, setGroup] = useAtom(groupAtom);
  const [, setSelectedGroup] = useSelectedGroupQuery();

  const clearGroup = useCallback(() => {
    setGroup((prevGroup) => {
      // When resetting we want to persist the last known type so we know what list to display in the dashboard
      const { type, mercatorMetropolitanId, mercator_metropolitan_ids, state_code } =
        prevGroup;
      const resetGroup = {
        ...DEFAULT_GROUP,
        type,
        mercatorMetropolitanId,
        mercator_metropolitan_ids,
        state_code,
      };
      setSelectedGroup('');

      return resetGroup;
    });
  }, [setGroup]);

  return { clearGroup };
};

// Actions
export const useDashboard = () => {
  const [, setId] = useDashboardGroupId();
  const [, setType] = useDashboardGroupType();
  const [group, setGroup] = useAtom(groupAtom);
  const [loading, setLoading] = useState(false);
  const { clearGroup } = useClearGroup();
  const [selectedPolygonBoundsOnMap] = useSelectedPolygonBoundsOnMap();
  const store = useStore();

  const saveGroup = useCallback(() => {
    // Read the group straight from the store rather than the render closure.
    // Inputs (e.g. the group name) commit to the atom on blur, which happens
    // synchronously as the save button is clicked — before this component
    // re-renders — so the closure would still hold the pre-edit value and the
    // user would have to click "save" twice.
    const currentGroup = store.get(groupAtom);

    return GroupConfigurationAPI.postCreateNewSavedSearch(
      groupToGroupRaw({ ...currentGroup, polygon_bounds: selectedPolygonBoundsOnMap }),
      { timeout: 30000 },
    );
  }, [store, selectedPolygonBoundsOnMap]);

  const deleteGroup = useCallback(
    (groupId: string) => {
      return GroupConfigurationAPI.deleteGroupConfiguration(groupId);
    },
    [group],
  );

  const updateGroup = useCallback(async () => {
    setLoading(true);
    // See saveGroup: read from the store so a name just committed on blur is
    // included on the first save click.
    const currentGroup = store.get(groupAtom);
    try {
      const response = await GroupConfigurationAPI.patchGroupConfiguration(
        String(currentGroup.id),
        groupToGroupRaw({ ...currentGroup, polygon_bounds: selectedPolygonBoundsOnMap }),
        { timeout: 30000 },
      );
      setLoading(false);

      return response;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, [store, selectedPolygonBoundsOnMap]);

  const startNewProject = useCallback(
    (type: GROUP_TYPE) => {
      // Clear any lingering state
      clearGroup();
      setId(NEW_GROUP_ID);
      setType(type);
    },
    [clearGroup, setId, setType],
  );

  const saveCities = useCallback(
    (cityIds: number[], keysToUpdate: CityUpdateKeys) => {
      setGroup((v) => {
        const updatedGroup = { ...v };

        if (keysToUpdate.triggers) {
          updatedGroup.triggers = cityIds.map((id) => ({
            cityId: id,
            documentTypes: [],
          }));
        }

        if (keysToUpdate.exclude_past_project_stage) {
          updatedGroup.exclude_past_project_stage = cityIds.map((id) => ({
            cityId: id,
            documentTypes: [],
          }));
        }

        if (keysToUpdate.past_project_stage) {
          updatedGroup.past_project_stage = cityIds.map((id) => ({
            cityId: id,
            documentTypes: [],
          }));
        }

        return updatedGroup;
      });
    },
    [setGroup],
  );

  return {
    clearGroup,
    saveCities,
    saveGroup,
    updateGroup,
    loading,
    deleteGroup,
    startNewProject,
    group,
  };
};

export async function fetchGroup(id?: string) {
  if (!id || id === NEW_GROUP_ID) return undefined;
  clear();
  const response = await GroupConfigurationAPI.fetchGroupConfiguration(id);

  return response?.data?.data;
}

export const useDashboardKeywordsAndExclusions = () => {
  const [keywords, setKeywords] = useAtom(groupKeywordsAtom);
  const [exclusions, setExclusions] = useAtom(groupExclusionsAtom);

  const addItems = (existingItems: string, newItems: string) => {
    const existingList = existingItems.split(',').filter(Boolean);
    const newList = newItems.split(',').filter(Boolean);

    const mergedItems = Array.from(new Set([...existingList, ...newList]));

    return mergedItems.join(',');
  };

  const removeItem = (existingItems: string, itemToRemove: string) => {
    const existingList = existingItems.split(',').filter(Boolean);

    const updatedList = existingList.filter((item) => item !== itemToRemove);

    return updatedList.join(',');
  };

  const updateKeywords = useCallback(
    (newKeywords: string) => {
      setKeywords((currentKeywords) => {
        return newKeywords !== '' ? addItems(currentKeywords, newKeywords) : '';
      });
    },
    [setKeywords],
  );

  const updateExclusions = useCallback(
    (newExclusions: string) => {
      setExclusions((currentExclusions) => {
        return newExclusions !== ''
          ? addItems(currentExclusions || '', newExclusions)
          : '';
      });
    },
    [setExclusions],
  );

  const removeKeyword = useCallback(
    (keywordToRemove: string) => {
      setKeywords((currentKeywords) => {
        return removeItem(currentKeywords, keywordToRemove);
      });
    },
    [setKeywords],
  );

  const removeExclusion = useCallback(
    (exclusionToRemove: string) => {
      setExclusions((currentExclusions) => {
        return removeItem(currentExclusions || '', exclusionToRemove);
      });
    },
    [setExclusions],
  );

  return {
    keywords,
    updateKeywords,
    removeKeyword,
    exclusions,
    updateExclusions,
    removeExclusion,
  };
};

export const useDashboardCompanyKeywordsAndExclusions = () => {
  const [keywords, setKeywords] = useAtom(groupCompanyKeywordsAtom);
  const [exclusions, setExclusions] = useAtom(groupCompanyKeywordsExclusionsAtom);

  const addItems = (existingItems: string, newItems: string) => {
    const existingList = existingItems.split(',').filter(Boolean);
    const newList = newItems.split(',').filter(Boolean);

    const mergedItems = Array.from(new Set([...existingList, ...newList]));

    return mergedItems.join(',');
  };

  const removeItem = (existingItems: string, itemToRemove: string) => {
    const existingList = existingItems.split(',').filter(Boolean);

    const updatedList = existingList.filter((item) => item !== itemToRemove);

    return updatedList.join(',');
  };

  const updateKeywords = useCallback(
    (newKeywords: string) => {
      setKeywords((currentKeywords) => {
        return newKeywords !== '' ? addItems(currentKeywords, newKeywords) : '';
      });
    },
    [setKeywords],
  );

  const updateExclusions = useCallback(
    (newExclusions: string) => {
      setExclusions((currentExclusions) => {
        return newExclusions !== ''
          ? addItems(currentExclusions || '', newExclusions)
          : '';
      });
    },
    [setExclusions],
  );

  const removeKeyword = useCallback(
    (keywordToRemove: string) => {
      setKeywords((currentKeywords) => {
        return removeItem(currentKeywords, keywordToRemove);
      });
    },
    [setKeywords],
  );

  const removeExclusion = useCallback(
    (exclusionToRemove: string) => {
      setExclusions((currentExclusions) => {
        return removeItem(currentExclusions || '', exclusionToRemove);
      });
    },
    [setExclusions],
  );

  return {
    keywords,
    updateKeywords,
    removeKeyword,
    exclusions,
    updateExclusions,
    removeExclusion,
  };
};
